import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { BarChart3, Users, DollarSign, FolderOpen, BookOpen, FileText, Mail } from 'lucide-react';
import { Helmet } from 'react-helmet';
import { db } from '@/config/firebase';
import { collection, query, getDocs, where, orderBy, limit, collectionGroup } from 'firebase/firestore';
import BlogManager from '@/components/Admin/BlogManager';
import GuideManager from '@/components/Admin/GuideManager';
import EmailTemplateEditor from '@/components/admin/EmailTemplateEditor';

const Admin = () => {
  const [metrics, setMetrics] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      // Fetch users
      const usersRef = collection(db, 'users');
      const usersQuery = query(usersRef, orderBy('createdAt', 'desc'), limit(10));
      const usersSnapshot = await getDocs(usersQuery);
      const usersList = [];
      usersSnapshot.forEach((doc) => {
        usersList.push({ id: doc.id, ...doc.data() });
      });
      setUsers(usersList);

      // Calculate metrics
      const allUsersSnapshot = await getDocs(collection(db, 'users'));
      const totalUsers = allUsersSnapshot.size;

      // Get total projects (using collectionGroup for all projects across all users)
      const projectsSnapshot = await getDocs(collectionGroup(db, 'projects'));
      const totalProjects = projectsSnapshot.size;

      // Get active subscriptions
      const activeSubsQuery = query(
        collection(db, 'users'),
        where('subscription.status', '==', 'active'),
        where('subscription.plan', '!=', 'free')
      );
      const activeSubsSnapshot = await getDocs(activeSubsQuery);
      const activeSubscriptions = activeSubsSnapshot.size;

      // Get total revenue
      const paymentsSnapshot = await getDocs(collection(db, 'payments'));
      let totalRevenue = 0;
      paymentsSnapshot.forEach((doc) => {
        totalRevenue += doc.data().amount || 0;
      });

      setMetrics({
        totalUsers,
        totalProjects,
        activeSubscriptions,
        totalRevenue,
      });
    } catch (error) {
      console.error('Error fetching admin data:', error);
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading admin dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Admin Dashboard - Fluxara</title>
      </Helmet>

      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="mb-8">
            <h1 className="text-4xl font-bold mb-2 flex items-center gap-2">
              <BarChart3 className="w-8 h-8 text-cyan-600" />
              Admin Dashboard
            </h1>
            <p className="text-gray-600">Platform overview and content management</p>
          </div>

          <Tabs defaultValue="dashboard" className="w-full">
            <TabsList className="grid w-full grid-cols-4 mb-8">
              <TabsTrigger value="dashboard" className="flex items-center gap-2">
                <BarChart3 className="w-4 h-4" />
                Dashboard
              </TabsTrigger>
              <TabsTrigger value="blogs" className="flex items-center gap-2">
                <BookOpen className="w-4 h-4" />
                Blogs
              </TabsTrigger>
              <TabsTrigger value="guides" className="flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Guides
              </TabsTrigger>
              <TabsTrigger value="emails" className="flex items-center gap-2">
                <Mail className="w-4 h-4" />
                Emails
              </TabsTrigger>
            </TabsList>

            <TabsContent value="dashboard">
              {/* Metrics Cards */}
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-gray-600">Total Users</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center">
                      <Users className="w-8 h-8 text-cyan-600 mr-3" />
                      <p className="text-3xl font-bold">{metrics?.totalUsers || 0}</p>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-gray-600">Total Projects</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center">
                      <FolderOpen className="w-8 h-8 text-blue-600 mr-3" />
                      <p className="text-3xl font-bold">{metrics?.totalProjects || 0}</p>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-gray-600">Active Subscriptions</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center">
                      <BarChart3 className="w-8 h-8 text-green-600 mr-3" />
                      <p className="text-3xl font-bold">{metrics?.activeSubscriptions || 0}</p>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-gray-600">Total Revenue</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center">
                      <DollarSign className="w-8 h-8 text-yellow-600 mr-3" />
                      <p className="text-3xl font-bold">${metrics?.totalRevenue?.toFixed(2) || '0.00'}</p>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Users Table */}
              <Card>
                <CardHeader>
                  <CardTitle>Recent Users</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-3 px-4">User</th>
                          <th className="text-left py-3 px-4">Email</th>
                          <th className="text-left py-3 px-4">Plan</th>
                          <th className="text-left py-3 px-4">Credits</th>
                          <th className="text-left py-3 px-4">Joined</th>
                        </tr>
                      </thead>
                      <tbody>
                        {users.slice(0, 10).map((user) => (
                          <tr key={user.id} className="border-b hover:bg-gray-50">
                            <td className="py-3 px-4">{user.displayName || 'N/A'}</td>
                            <td className="py-3 px-4">{user.email}</td>
                            <td className="py-3 px-4 capitalize">{user.subscription?.plan || 'free'}</td>
                            <td className="py-3 px-4">{user.credits || 0}</td>
                            <td className="py-3 px-4">
                              {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'N/A'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="blogs">
              <BlogManager />
            </TabsContent>

            <TabsContent value="guides">
              <GuideManager />
            </TabsContent>

            <TabsContent value="emails">
              <EmailTemplateEditor />
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </>
  );
};

export default Admin;